/**
 * UTM Parameter Tracker for Shopify
 * Preserves UTM parameters across sessions and includes them in order metafields
 */

(function() {
  'use strict';

  const UTMTracker = {
    // UTM parameters to track
    utmParams: ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'],
    
    // Storage key for localStorage
    storageKey: 'monka_utm_data',
    
    // Cookie expiry (30 days)
    cookieExpiry: 30,

    /**
     * Initialize UTM tracking
     */
    init: function() {
      this.captureUTMParams();
      this.addUTMToForms();
      this.addUTMToCheckout();
    },

    /**
     * Get URL parameters
     */
    getURLParams: function() {
      const params = {};
      const urlParams = new URLSearchParams(window.location.search);
      
      this.utmParams.forEach(param => {
        if (urlParams.has(param)) {
          params[param] = urlParams.get(param);
        }
      });
      
      return params;
    },

    /**
     * Capture and store UTM parameters
     */
    captureUTMParams: function() {
      const currentUTMs = this.getURLParams();
      
      // Only update if we have new UTM parameters
      if (Object.keys(currentUTMs).length > 0) {
        const utmData = {
          ...currentUTMs,
          timestamp: new Date().toISOString(),
          landing_page: window.location.href
        };
        
        // Store in localStorage
        localStorage.setItem(this.storageKey, JSON.stringify(utmData));
        
        // Store in cookie as backup
        this.setCookie(this.storageKey, JSON.stringify(utmData), this.cookieExpiry);
      }
    },

    /**
     * Get stored UTM data
     */
    getStoredUTMData: function() {
      let utmData = null;
      
      // Try localStorage first
      try {
        const stored = localStorage.getItem(this.storageKey);
        if (stored) {
          utmData = JSON.parse(stored);
        }
      } catch (e) {
        console.warn('UTM Tracker: Error reading from localStorage', e);
      }
      
      // Fallback to cookie
      if (!utmData) {
        try {
          const cookieData = this.getCookie(this.storageKey);
          if (cookieData) {
            utmData = JSON.parse(cookieData);
          }
        } catch (e) {
          console.warn('UTM Tracker: Error reading from cookie', e);
        }
      }
      
      return utmData;
    },

    /**
     * Add UTM data to forms (contact, newsletter, etc.)
     */
    addUTMToForms: function() {
      const utmData = this.getStoredUTMData();
      if (!utmData) return;

      // Find all forms that might need UTM data
      const forms = document.querySelectorAll('form[action*="contact"], form[action*="newsletter"], form.contact-form');
      
      forms.forEach(form => {
        // Add UTM data as hidden fields
        Object.keys(utmData).forEach(key => {
          if (!form.querySelector(`input[name="${key}"]`)) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = utmData[key];
            form.appendChild(input);
          }
        });
      });
    },

    /**
     * Add UTM data to checkout process
     */
    addUTMToCheckout: function() {
      const utmData = this.getStoredUTMData();
      if (!utmData) return;

      // Hook into Shopify's checkout process
      if (typeof Shopify !== 'undefined' && Shopify.checkout) {
        // Add UTM data to checkout attributes
        const checkoutData = {};
        Object.keys(utmData).forEach(key => {
          checkoutData[`UTM_${key}`] = utmData[key];
        });

        // Update checkout with UTM data
        if (typeof ShopifyAPI !== 'undefined') {
          ShopifyAPI.checkout.updateAttributes(checkoutData);
        }
      }

      // Add UTM data to cart attributes for checkout
      this.addUTMToCart();
    },

    /**
     * Add UTM data to cart attributes
     */
    addUTMToCart: function() {
      const utmData = this.getStoredUTMData();
      if (!utmData) return;

      // Prepare cart attributes
      const attributes = {};
      Object.keys(utmData).forEach(key => {
        attributes[`UTM_${key}`] = utmData[key];
      });

      // Update cart attributes via AJAX
      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes: attributes })
      }).catch(error => {
        console.warn('UTM Tracker: Error updating cart attributes', error);
      });
    },

    /**
     * Add UTM parameters to all internal links
     */
    preserveUTMInLinks: function() {
      const utmData = this.getStoredUTMData();
      if (!utmData) return;

      const links = document.querySelectorAll('a[href]');
      links.forEach(link => {
        const href = link.getAttribute('href');
        
        // Only modify internal links
        if (href && !href.startsWith('http') && !href.startsWith('mailto:') && !href.startsWith('tel:')) {
          const url = new URL(href, window.location.origin);
          
          // Add UTM parameters to the URL
          Object.keys(utmData).forEach(key => {
            if (this.utmParams.includes(key)) {
              url.searchParams.set(key, utmData[key]);
            }
          });
          
          link.setAttribute('href', url.pathname + url.search);
        }
      });
    },

    /**
     * Set cookie
     */
    setCookie: function(name, value, days) {
      const expires = new Date();
      expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
    },

    /**
     * Get cookie
     */
    getCookie: function(name) {
      const nameEQ = name + "=";
      const ca = document.cookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
      }
      return null;
    },

    /**
     * Debug function to log current UTM data
     */
    debug: function() {
      const utmData = this.getStoredUTMData();
      console.log('UTM Tracker - Stored Data:', utmData);
      return utmData;
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      UTMTracker.init();
    });
  } else {
    UTMTracker.init();
  }

  // // Preserve UTM in links after page load
  // window.addEventListener('load', function() {
  //   UTMTracker.preserveUTMInLinks();
  // });

  // Expose UTMTracker globally for debugging
  window.UTMTracker = UTMTracker;

})();
